const COUNT_SELECTOR = '[data-count-animate]';
const DEFAULT_DURATION = 1600;
const DEFAULT_THRESHOLD = 0.35;

const readyElements = new WeakSet();
const pendingElements = new WeakSet();
const animatedElements = new WeakSet();
const observedElements = new WeakSet();
const observers = new Map();

let fontsReadyPromise = null;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeNumberText = (value) => value.trim().replace(/[\u00A0\u202F]/g, ' ');

const getGroupedSeparator = (value) => {
  const apostropheMatch = value.match(/\d([\u2019'])\d/);

  if (apostropheMatch) {
    return apostropheMatch[1];
  }

  const spaceMatch = value.match(/\d( )\d/);

  if (spaceMatch) {
    return spaceMatch[1];
  }

  return '';
};

const getDecimalPartLength = (value, decimalSeparator) => {
  if (!decimalSeparator) {
    return 0;
  }

  const parts = value.split(decimalSeparator);
  return parts[1] ? parts[1].length : 0;
};

export const parseSingleNumber = (numberText) => {
  const numberPart = normalizeNumberText(numberText);

  if (!numberPart) {
    return null;
  }

  const hasComma = numberPart.includes(',');
  const hasDot = numberPart.includes('.');
  const groupedSeparator = getGroupedSeparator(numberPart);

  let decimalSeparator = null;
  let thousandsSeparator = groupedSeparator;

  if (hasComma && hasDot) {
    const lastCommaIndex = numberPart.lastIndexOf(',');
    const lastDotIndex = numberPart.lastIndexOf('.');

    if (lastCommaIndex > lastDotIndex) {
      decimalSeparator = ',';
      thousandsSeparator = thousandsSeparator || '.';
    } else {
      decimalSeparator = '.';
      thousandsSeparator = thousandsSeparator || ',';
    }
  } else if (hasComma) {
    const commaParts = numberPart.split(',');

    if (commaParts.length === 2 && commaParts[1].length > 0 && commaParts[1].length <= 2) {
      decimalSeparator = ',';
    } else {
      thousandsSeparator = thousandsSeparator || ',';
    }
  } else if (hasDot) {
    const dotParts = numberPart.split('.');

    if (dotParts.length === 2 && dotParts[1].length > 0 && dotParts[1].length <= 2) {
      decimalSeparator = '.';
    } else {
      thousandsSeparator = thousandsSeparator || '.';
    }
  }

  let cleanNumber = numberPart;

  if (thousandsSeparator) {
    const thousandsRegexp = new RegExp(escapeRegExp(thousandsSeparator), 'g');
    cleanNumber = cleanNumber.replace(thousandsRegexp, '');
  }

  if (decimalSeparator && decimalSeparator !== '.') {
    const decimalRegexp = new RegExp(escapeRegExp(decimalSeparator), 'g');
    cleanNumber = cleanNumber.replace(decimalRegexp, '.');
  }

  const value = Number.parseFloat(cleanNumber);

  if (Number.isNaN(value)) {
    return null;
  }

  return {
    value,
    decimals: getDecimalPartLength(numberPart, decimalSeparator),
    decimalSeparator,
    thousandsSeparator,
  };
};

export const parseCountText = (text) => {
  const numberRegexp = /[-+]?\d(?:[\d\s,.'\u2019]*\d)?/g;
  const parts = [];
  let lastIndex = 0;
  let numberIndex = 0;
  let match = null;

  while ((match = numberRegexp.exec(text)) !== null) {
    const matchedText = match[0];
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      parts.push({
        type: 'text',
        value: text.slice(lastIndex, matchIndex),
      });
    }

    const parsedNumber = parseSingleNumber(matchedText);

    if (parsedNumber) {
      parts.push({
        type: 'number',
        index: numberIndex,
        ...parsedNumber,
      });
      numberIndex += 1;
    } else {
      parts.push({
        type: 'text',
        value: matchedText,
      });
    }

    lastIndex = matchIndex + matchedText.length;
  }

  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      value: text.slice(lastIndex),
    });
  }

  const numberParts = parts.filter((part) => part.type === 'number');

  if (!numberParts.length) {
    return null;
  }

  return {
    parts,
    numberParts,
  };
};

export const formatCountValue = (
  value,
  decimals,
  decimalSeparator,
  thousandsSeparator
) => {
  const formattedValue = value.toFixed(decimals);
  let [integerPart, fractionPart] = formattedValue.split('.');

  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator || '');

  if (decimals > 0 && fractionPart !== undefined) {
    return integerPart + (decimalSeparator || '.') + fractionPart;
  }

  return integerPart;
};

export const buildCountText = (parsedData, values) => parsedData.parts.map((part) => {
  if (part.type === 'text') {
    return part.value;
  }

  return formatCountValue(
    values[part.index],
    part.decimals,
    part.decimalSeparator,
    part.thousandsSeparator
  );
}).join('');

const parsePositiveInteger = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }

  return parsed;
};

const parseThreshold = (value) => {
  const parsed = Number.parseFloat(value);

  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return DEFAULT_THRESHOLD;
  }

  return parsed;
};

export const getCountOptions = (element) => {
  const dataset = element?.dataset || {};
  const startValue = Number.parseFloat(dataset.countStart);

  return {
    duration: parsePositiveInteger(dataset.countDuration, DEFAULT_DURATION),
    startValue: Number.isNaN(startValue) ? null : startValue,
    threshold: parseThreshold(dataset.countThreshold),
    lockWidth: Boolean(element?.hasAttribute?.('data-count-lock-width')),
  };
};

const getCountElements = (scope) => {
  const currentScope = scope || (typeof document !== 'undefined' ? document : null);

  if (!currentScope) {
    return [];
  }

  if (currentScope.matches?.(COUNT_SELECTOR)) {
    return [currentScope];
  }

  return Array.from(currentScope.querySelectorAll?.(COUNT_SELECTOR) || []);
};

const getTime = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }

  return Date.now();
};

const requestFrame = (callback) => {
  if (typeof requestAnimationFrame === 'function') {
    return requestAnimationFrame(callback);
  }

  return setTimeout(() => callback(getTime()), 16);
};

const prepareVisibility = (element) => {
  if (readyElements.has(element) || pendingElements.has(element) || animatedElements.has(element)) {
    return;
  }

  element.style.opacity = '0';
  element.style.transition = 'opacity 0.3s ease';
  pendingElements.add(element);
};

const lockElementWidth = (element) => {
  if (!getCountOptions(element).lockWidth) {
    return;
  }

  const width = element.offsetWidth;

  if (!width) {
    return;
  }

  if (typeof getComputedStyle === 'function' && getComputedStyle(element).display === 'inline') {
    element.style.display = 'inline-block';
  }

  element.style.minWidth = `${width}px`;
};

const revealElement = (element) => {
  requestFrame(() => {
    element.style.opacity = '1';
  });
};

const prepareElement = (element) => {
  if (readyElements.has(element)) {
    return;
  }

  pendingElements.delete(element);
  lockElementWidth(element);
  revealElement(element);
  element.classList.add('is-count-ready');
  readyElements.add(element);
};

const waitForFonts = () => {
  if (fontsReadyPromise) {
    return fontsReadyPromise;
  }

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    fontsReadyPromise = document.fonts.ready.catch(() => Promise.resolve());
    return fontsReadyPromise;
  }

  fontsReadyPromise = new Promise((resolve) => {
    if (typeof window !== 'undefined') {
      window.addEventListener('load', resolve, { once: true });
      setTimeout(resolve, 1200);
      return;
    }

    resolve();
  });

  return fontsReadyPromise;
};

const animateCount = (element, parsedData = parseCountText(element.textContent || '')) => {
  if (!element || animatedElements.has(element) || element.classList.contains('is-count-animated')) {
    return;
  }

  if (!parsedData) {
    return;
  }

  animatedElements.add(element);
  element.classList.add('is-count-animated');

  const { duration, startValue } = getCountOptions(element);
  const targetValues = parsedData.numberParts.map((part) => part.value);
  const startValues = parsedData.numberParts.map((part, index) => {
    if (index === 0 && startValue !== null) {
      return startValue;
    }

    return 0;
  });
  const startTime = getTime();

  const stepAnimation = (currentTime) => {
    const progress = Math.min((currentTime - startTime) / duration, 1);
    const easedProgress = 1 - Math.pow(1 - progress, 2);
    const currentValues = targetValues.map((targetValue, index) => {
      const currentStartValue = startValues[index];

      return currentStartValue + (targetValue - currentStartValue) * easedProgress;
    });

    element.textContent = buildCountText(parsedData, currentValues);

    if (progress < 1) {
      requestFrame(stepAnimation);
      return;
    }

    element.textContent = buildCountText(parsedData, targetValues);
  };

  requestFrame(stepAnimation);
};

const getObserver = (threshold) => {
  const key = String(threshold);

  if (observers.has(key)) {
    return observers.get(key);
  }

  const observer = new IntersectionObserver((entries, currentObserver) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return;
      }

      observedElements.delete(entry.target);
      animateCount(entry.target);
      currentObserver.unobserve(entry.target);
    });
  }, {
    threshold,
  });

  observers.set(key, observer);
  return observer;
};

const observeElement = (element) => {
  if (animatedElements.has(element) || observedElements.has(element)) {
    return;
  }

  const parsedData = parseCountText(element.textContent || '');

  if (!parsedData) {
    return;
  }

  if (typeof IntersectionObserver !== 'function') {
    animateCount(element, parsedData);
    return;
  }

  observedElements.add(element);
  getObserver(getCountOptions(element).threshold).observe(element);
};

export const initCountAnimation = (scope) => {
  const elements = getCountElements(scope);

  if (!elements.length) {
    return;
  }

  elements.forEach(prepareVisibility);

  waitForFonts().then(() => {
    elements.forEach((element) => {
      prepareElement(element);
      observeElement(element);
    });
  });
};

if (typeof window !== 'undefined') {
  window.initCountAnimation = initCountAnimation;
}

if (typeof document !== 'undefined') {
  initCountAnimation();
}
